import { useMutation } from '@tanstack/react-query';
import { Mic, PhoneCall, PhoneOff, Send } from 'lucide-react';
import { Alert, Badge, Button, Card, CardContent, Input, PageHeader } from '@buzzbee/ui';
import { useEffect, useRef, useState } from 'react';
import { errorMessage, post } from '../lib/api';

type Line = { who: 'agent' | 'caller' | 'tool'; text: string };

/** Browser test call: the real agent runtime and LLM (stub without a key), voice mocked as text + speech synthesis. */
export function TestCall({ onBooked }: { onBooked?: () => void }) {
  const [conv, setConv] = useState<string | null>(null);
  const [llm, setLlm] = useState<string>('');
  const [lines, setLines] = useState<Line[]>([]);
  const [text, setText] = useState('');
  const [speak, setSpeak] = useState(true);
  const logRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [lines]);
  const say = (t: string) => {
    if (!speak || typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(new SpeechSynthesisUtterance(t));
  };
  const start = useMutation({
    mutationFn: () => post<{ conversationId: string; openingLine: string; llm: string }>('/api/simulator/start', {}),
    onSuccess: (r) => {
      setConv(r.conversationId);
      setLlm(r.llm);
      setLines([{ who: 'agent', text: r.openingLine }]);
      say(r.openingLine);
    },
  });
  const turn = useMutation({
    mutationFn: (utterance: string) => post<{ reply: string; toolCalls: { tool: string }[]; outcome: string }>(`/api/simulator/${conv}/turn`, { text: utterance }),
    onSuccess: (r) => {
      setLines((l) => [...l, ...r.toolCalls.map((t) => ({ who: 'tool' as const, text: t.tool.replace(/_/g, ' ') })), { who: 'agent', text: r.reply }]);
      say(r.reply);
      if (r.outcome === 'booked') onBooked?.();
    },
  });
  const end = useMutation({ mutationFn: () => post(`/api/simulator/${conv}/end`, {}), onSuccess: () => setConv(null) });
  const send = () => {
    const t = text.trim();
    if (!t || !conv) return;
    setLines((l) => [...l, { who: 'caller', text: t }]);
    setText('');
    turn.mutate(t);
  };
  const listen = () => {
    const Rec = (window as unknown as { SpeechRecognition?: new () => any; webkitSpeechRecognition?: new () => any }).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
    if (!Rec) return;
    const r = new Rec();
    r.lang = 'en-CA';
    r.onresult = (e: any) => setText(e.results[0][0].transcript);
    r.start();
  };
  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex flex-wrap items-center gap-2">
          {conv ? <Badge variant="success">On call</Badge> : <Badge>Idle</Badge>}
          {llm ? <Badge variant="outline">{llm === 'stub' ? 'Offline test model (set ANTHROPIC_API_KEY for Claude)' : 'Claude'}</Badge> : null}
          <label className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" checked={speak} onChange={(e) => setSpeak(e.target.checked)} /> Speak replies
          </label>
        </div>
        <div ref={logRef} className="h-80 space-y-2 overflow-y-auto rounded-lg bg-muted/50 p-3" aria-live="polite">
          {lines.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">Start a test call to hear your receptionist. Try "Can I book a massage tomorrow at 2pm?" or "What are your hours?"</p> : null}
          {lines.map((l, i) =>
            l.who === 'tool' ? (
              <p key={i} className="text-center text-xs text-muted-foreground">
                used {l.text}
              </p>
            ) : (
              <div key={i} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${l.who === 'caller' ? 'ml-auto bg-primary text-primary-foreground' : 'bg-card shadow-card'}`}>
                {l.text}
              </div>
            ),
          )}
        </div>
        {start.error || turn.error ? <Alert variant="destructive">{errorMessage(start.error ?? turn.error)}</Alert> : null}
        {conv ? (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <Input aria-label="What the caller says" placeholder="Say something as the caller..." value={text} onChange={(e) => setText(e.target.value)} />
            <Button type="button" variant="outline" size="icon" aria-label="Speak" onClick={listen}>
              <Mic />
            </Button>
            <Button type="submit" loading={turn.isPending} aria-label="Send">
              <Send />
            </Button>
            <Button type="button" variant="destructive" size="icon" aria-label="Hang up" onClick={() => end.mutate()}>
              <PhoneOff />
            </Button>
          </form>
        ) : (
          <Button onClick={() => start.mutate()} loading={start.isPending}>
            <PhoneCall /> Start test call
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function TestCallPage() {
  return (
    <div className="space-y-6">
      <PageHeader title="Test call" description="Talk to your receptionist in the browser. It uses your real services, hours and calendar; test bookings are marked and no minutes are billed." />
      <TestCall />
    </div>
  );
}
